import type { AccountRecord, StoreRecord } from "../organization/organization.types";
import { buildMemberTemplateInBackground, parseMemberWorkbookInBackground } from "./excelWorkerClient";
export { excelCellText as cellText } from "./excelCells";

export type MemberImportRow = {
  rowNumber: number;
  name: string;
  phone: string;
  gender: "male" | "female" | "undisclosed";
  birthday: string;
  storeCode: string;
  coachPhone: string;
  sourceMemberId: string;
  note: string;
};

export type CheckedMemberImportRow = MemberImportRow & {
  storeId: string | null;
  coachId: string | null;
  errors: string[];
};

export async function downloadMemberImportTemplate() {
  const buffer = await buildMemberTemplateInBackground();
  downloadBlob(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), "网爆星球V4-会员导入模板.xlsx");
}

export async function parseMemberImportFile(file: File): Promise<MemberImportRow[]> {
  const records = file.name.toLowerCase().endsWith(".csv")
    ? parseCsv(await file.text())
    : await parseWorkbook(file);
  if (!records.length) throw new Error("文件中没有会员数据。");
  return records.map((record, index) => normalizeRow(record, index + 2));
}

async function parseWorkbook(file: File) {
  return parseMemberWorkbookInBackground(await file.arrayBuffer());
}

function parseCsv(text: string) {
  const lines = text.replace(/^\uFEFF/, "").split(/\r?\n/).filter((line) => line.trim());
  const rows = lines.map(parseCsvLine);
  const header = rows.shift() ?? [];
  return rows.map((row) => Object.fromEntries(header.map((key, index) => [key.trim(), row[index]?.trim() ?? ""])));
}

function parseCsvLine(line: string) {
  const cells: string[] = []; let value = ""; let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (char === '"' && quoted && line[index + 1] === '"') { value += '"'; index += 1; }
    else if (char === '"') quoted = !quoted;
    else if (char === "," && !quoted) { cells.push(value); value = ""; }
    else value += char;
  }
  cells.push(value); return cells;
}

function normalizeRow(record: Record<string, string>, rowNumber: number): MemberImportRow {
  const genderText = record["性别"]?.trim();
  return {
    rowNumber,
    name: record["会员姓名"]?.trim() ?? "",
    phone: record["手机号"]?.replace(/\D/g, "") ?? "",
    gender: genderText === "男" || genderText === "male" ? "male" : genderText === "女" || genderText === "female" ? "female" : "undisclosed",
    birthday: normalizeDate(record["生日"]),
    storeCode: record["门店编码"]?.trim() ?? "",
    coachPhone: record["主教练手机号"]?.replace(/\D/g, "") ?? "",
    sourceMemberId: record["原系统会员编号"]?.trim() ?? "",
    note: record["备注"]?.trim() ?? "",
  };
}

export function validateMemberImport(rows: MemberImportRow[], stores: StoreRecord[], accounts: AccountRecord[]): CheckedMemberImportRow[] {
  const storesByCode = new Map(stores.map((store) => [store.code.toLowerCase(), store]));
  const accountsByPhone = new Map(accounts.map((account) => [account.phone, account]));
  const batchPhones = new Set<string>();
  return rows.map((row) => {
    const errors: string[] = [];
    const store = storesByCode.get(row.storeCode.toLowerCase());
    const coach = accountsByPhone.get(row.coachPhone);
    if (!row.name || row.name.length > 80) errors.push("会员姓名缺失或超过80字");
    if (!/^1\d{10}$/.test(row.phone)) errors.push("手机号格式错误");
    if (batchPhones.has(row.phone)) errors.push("文件内手机号重复"); else batchPhones.add(row.phone);
    if (accountsByPhone.has(row.phone)) errors.push(`手机号已绑定${roleLabel(accountsByPhone.get(row.phone)!.role)}账号`);
    if (row.birthday && row.birthday > new Date().toISOString().slice(0, 10)) errors.push("生日不能晚于今天");
    if (!store || store.status !== "active") errors.push("门店编码不存在或未启用");
    if (!coach || !["employee", "manager"].includes(coach.role) || coach.status !== "active") errors.push("主教练手机号未匹配到启用教练或店长");
    else if (store && coach.storeId !== store.id && coach.role !== "employee") errors.push("跨店主教练必须为启用员工");
    return { ...row, storeId: store?.id ?? null, coachId: coach?.id ?? null, errors };
  });
}

function normalizeDate(value = "") { const matched = String(value).match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/); return matched ? `${matched[1]}-${matched[2].padStart(2, "0")}-${matched[3].padStart(2, "0")}` : ""; }
function roleLabel(role: string) { return ({ member: "会员", employee: "员工", manager: "店长", super: "超管" } as Record<string, string>)[role] ?? "其他"; }
function downloadBlob(blob: Blob, name: string) { const url = URL.createObjectURL(blob); const link = document.createElement("a"); link.href = url; link.download = name; link.click(); URL.revokeObjectURL(url); }
